import { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { useLocation, useParams, useSearchParams } from 'react-router';
import { useFetchDashboardView } from '../state/data/dashboard';
import { useMarkChatViewed } from '../state/data/outreach';
import { getDashboardTaskRoles } from '../data/roles';
import {
  applyBoardPick, BOARD_INCLUDE, REPORT_READY_VALUE, SENSITIVE_BOARD_KEYS,
} from '../logic/dashboard';
import { isSubtrackFilterValue } from '../logic/subtracks';
import useDebounce from './useDebounce';

// Board filters + sort mirror to the URL so a WF60 Chat deep link like
// /team/6719?alert=red,yellow opens the board pre-filtered. (Search stays local, not URL-synced.)
const URL_FILTER_KEYS = ['alert', 'lifecycle', 'engagement', 'conditions', 'open_task', 'oap_outcome', 'subtrack', 'report_ready'];

// apiv2 refuses an unknown sub-track code or a report_ready other than 1, so a hand-edited URL is
// cut down to what it accepts before anything is sent.
const URL_VALUE_CHECKS = {
  subtrack: isSubtrackFilterValue,
  report_ready: (v) => v === REPORT_READY_VALUE,
};

function parseFiltersFromParams(sp) {
  const sel = {};
  for (const k of URL_FILTER_KEYS) {
    const v = sp.get(k);
    if (!v) continue;
    const check = URL_VALUE_CHECKS[k];
    const arr = v.split(',').map(s => s.trim()).filter(Boolean).filter(x => !check || check(x));
    if (arr.length) sel[k] = arr;
  }
  return sel;
}

// The full set of query params the board owns. Other surfaces put their own state
// on the same query string (the patient file's `?tab=`/`?goto=`, Engage's
// `?patient=`), so anything navigating back to the board must carry THESE keys and
// drop the rest — hence one exported list rather than a copy per call site.
const BOARD_URL_KEYS = [...URL_FILTER_KEYS, 'sort', 'order'];

/**
 * Narrow a query string to the board's own filter + sort state. Used when
 * navigating between the board and the patient file so a filtered roster survives
 * the round trip without dragging the drawer's params back onto the board.
 * @param {string} [search] e.g. '?alert=red&tab=care-plan'
 * @returns {string} e.g. '?alert=red' ('' when nothing board-owned is set)
 */
export function pickBoardParams(search, { forPatient = false } = {}) {
  const from = new URLSearchParams(search || '');
  const next = new URLSearchParams();
  for (const k of BOARD_URL_KEYS) {
    if (forPatient && SENSITIVE_BOARD_KEYS.includes(k)) continue;
    const v = from.get(k);
    if (v) next.set(k, v);
  }
  const qs = next.toString();
  return qs ? `?${qs}` : '';
}

function parseSortFromParams(sp) {
  const field = sp.get('sort');
  if (!field) return { field: null, direction: null };
  const order = (sp.get('order') || '').toLowerCase();
  return { field, direction: order === 'desc' ? 'desc' : order === 'asc' ? 'asc' : null };
}

/**
 * Serialize filters + sort onto a copy of `base` (unrelated params preserved). Exported for unit tests.
 * @param {URLSearchParams} base
 * @param {{ selectedFilters?: Record<string, string[]>, sort?: { field: string | null, direction?: string | null } }} state
 * @param {{ omit?: readonly string[] }} [opts] keys left off the URL
 * @returns {URLSearchParams}
 */
export function serializeToParams(base, { selectedFilters = {}, sort = { field: null } }, { omit = [] } = {}) {
  const next = new URLSearchParams(base);
  for (const k of URL_FILTER_KEYS) {
    const vals = selectedFilters[k];
    if (Array.isArray(vals) && vals.length && !omit.includes(k)) next.set(k, vals.join(','));
    else next.delete(k);
  }
  if (sort.field) {
    next.set('sort', sort.field);
    if (sort.direction) next.set('order', sort.direction.toUpperCase());
    else next.delete('order');
  } else {
    next.delete('sort');
    next.delete('order');
  }
  return next;
}

function dedupeByUserid(list) {
  const seen = new Set();
  return list.filter(r => {
    const k = String(r.userid);
    if (seen.has(k)) return false;
    seen.add(k);
    return true;
  });
}

// Order-sensitive by design: both sides round-trip through serialize/parse which preserve order.
const filtersEqual = (a, b) => {
  const ak = Object.keys(a);
  if (ak.length !== Object.keys(b).length) return false;
  return ak.every(k => Array.isArray(b[k]) && a[k].length === b[k].length && a[k].every((v, i) => v === b[k][i]));
};

// Empty seed = URL carries no filter/sort, so the seeded state matches the unfiltered initialData
// and the first fetch can be skipped. A deep link WITH filters must fetch (else it's cosmetic).
function isEmptySeed(seed) {
  return Object.keys(seed.selectedFilters).length === 0 && !seed.sort.field;
}

export { parseFiltersFromParams, parseSortFromParams };

// The drawer's close sends this: the board takes back the filters the patient's URL could not carry.
// Any other way off a patient (sidebar Home, a link) starts from its own URL.
export const KEEP_BOARD_FILTERS_STATE = { keepBoardFilters: true };

// The sensitive filters a state carries, so a patient URL (which never shows them) can't drop them.
function sensitiveOf(selected) {
  return Object.fromEntries(SENSITIVE_BOARD_KEYS.filter(k => selected[k]?.length).map(k => [k, selected[k]]));
}

export default function useDashboardTable(userRole, initialData, allPatients) {
  const [searchParams, setSearchParams] = useSearchParams();
  // While a patient is open, the URL names them, so the diagnosis-level filters live in state only.
  const { withingsUserId } = useParams();
  const onPatient = !!withingsUserId;
  const closedPatient = !!useLocation().state?.keepBoardFilters;
  // Filters + sort parsed from the current URL — the seed + the reset/readback source of truth.
  const urlState = useMemo(() => ({
    selectedFilters: parseFiltersFromParams(searchParams),
    sort: parseSortFromParams(searchParams),
  }), [searchParams]);

  const [kpis, setKpis] = useState(initialData?.kpis || null);
  const [columns, setColumns] = useState(initialData?.columns || null);
  // What apiv2 says it honoured on the last answer; drives the roster columns and controls.
  const [includes, setIncludes] = useState(initialData?.includes || []);
  const [rows, setRows] = useState(initialData?.rows || []);
  const [pagination, setPagination] = useState(initialData?.pagination || null);
  const [filters, setFilters] = useState(initialData?.filters || []);
  // Map of filter key → string[] of selected values; empty/missing key = no filter. Seeded from URL.
  const [selectedFilters, setSelectedFilters] = useState(urlState.selectedFilters);
  const [loading, setLoading] = useState(false);
  // Appending the next page must NOT raise `loading` — that dims the whole table, which
  // reads as "the board is reloading" when the operator only scrolled.
  const [loadingMore, setLoadingMore] = useState(false);
  // Set once the server says there's nothing past the cursor, or once an append adds
  // nothing. Without the second case a page of pure duplicates wedges the roster: the
  // cursor is rows.length, so it stops advancing while `rows < total` still reads as
  // "more to fetch" — a live Load-more button that can never load.
  const [exhausted, setExhausted] = useState(false);
  const [searchInput, setSearchInput] = useState('');
  // URL ?sort= wins; else empty → server composite default (alert urgency → systolic → overdue).
  const [sort, setSort] = useState(urlState.sort);
  // `offset > 0` = an append (roster scrolled); offset 0 replaces. Sort/search/filter
  // changes reset it to 0, which is what discards the accumulated pages.
  const [page, setPage] = useState({ limit: 50, offset: 0 });
  const requestIdRef = useRef(0);
  // Carries "this is a full refresh" across the offset reset in `refresh()`, since the
  // actual fetch happens in the refetch effect rather than in the caller.
  const fullRefreshRef = useRef(false);
  // One-shot skip of the FIRST refetch when initialData is present AND the URL has no filters/sort
  // (initialData already is that board). Consumed once by the refetch effect; never re-armed —
  // re-arming it would silently drop the first user interaction on a bare board.
  const skipFirstFetchRef = useRef(!!initialData?.rows && isEmptySeed(urlState));

  const debouncedSearch = useDebounce(searchInput, 400);
  const fetchDashboardView = useFetchDashboardView();
  const { mutate: markChatViewed } = useMarkChatViewed();

  const rosterMap = useMemo(() => {
    const map = new Map();
    (allPatients || []).forEach(p => map.set(String(p.userid), p));
    return map;
  }, [allPatients]);

  const mergeWithRoster = useCallback((serverRows) => {
    return (serverRows || []).map(row => {
      const roster = rosterMap.get(String(row.userid)) || {};
      return {
        ...row,
        name: { first: row.name?.first || roster.firstname || '', last: row.name?.last || roster.lastname || '' },
      };
    });
  }, [rosterMap]);

  const fetchPage = useCallback(async (isFullRefresh = false) => {
    if (!userRole) return;
    const fullRefresh = isFullRefresh || fullRefreshRef.current;
    fullRefreshRef.current = false;
    const isAppend = page.offset > 0;
    const thisRequest = ++requestIdRef.current;
    if (isAppend) setLoadingMore(true); else setLoading(true);
    try {
      const result = await fetchDashboardView(userRole, {
        sort,
        pagination: page,
        search: debouncedSearch || undefined,
        task_roles: getDashboardTaskRoles(userRole),
        selectedFilters,
        include: BOARD_INCLUDE,
      });
      if (requestIdRef.current !== thisRequest) return; // stale
      if (result && result.status === 'success') {
        const merged = mergeWithRoster(result.rows);
        // Dedupe by userid on append: the roster is a live board, so a row that moved
        // across the page boundary between fetches would otherwise appear twice.
        const next = isAppend ? dedupeByUserid([...rows, ...merged]) : merged;
        setRows(next);
        setExhausted(
          result.pagination?.has_more === false || (isAppend && next.length === rows.length),
        );
        setPagination(result.pagination);
        setColumns(result.columns);
        setIncludes(result.includes || []);
        setFilters(result.filters || []);
        if (fullRefresh || !kpis) {
          setKpis(result.kpis);
        }
      }
    } catch (e) {
      if (requestIdRef.current !== thisRequest) return;
      console.warn('Dashboard fetch failed:', e.message);
    } finally {
      if (requestIdRef.current === thisRequest) {
        setLoading(false);
        setLoadingMore(false);
      }
    }
  }, [userRole, sort, page, debouncedSearch, selectedFilters, mergeWithRoster, kpis, rows, fetchDashboardView]);

  // Re-fetch on sort/search/page/filter change; skip only the first run when initialData matches.
  useEffect(() => {
    if (skipFirstFetchRef.current) {
      skipFirstFetchRef.current = false;
      return;
    }
    fetchPage(false);
    // deps intentionally narrowed to the refetch keys — fetchPage/userRole/kpis/mergeWithRoster
    // omitted to avoid refetch on their identity churn.
  }, [sort, page, debouncedSearch, selectedFilters]); // eslint-disable-line react-hooks/exhaustive-deps

  // Reset from initialData (role switch / first load). Filters+sort come from the URL (seed +
  // readback), not reset here. setPage bails when unchanged so a deep-link mount fetches once.
  // Does NOT touch skipFirstFetchRef — re-arming it would swallow the first interaction on a bare board.
  useEffect(() => {
    if (!initialData?.rows) return;
    setRows(mergeWithRoster(initialData.rows));
    setExhausted(initialData.pagination?.has_more === false);
    setPagination(initialData.pagination);
    setColumns(initialData.columns);
    setIncludes(initialData.includes || []);
    setFilters(initialData.filters || []);
    setKpis(initialData.kpis);
    setPage(p => (p.offset === 0 && p.limit === 50 ? p : { limit: 50, offset: 0 }));
    // keyed on initialData only; URL→state is owned by the readback effect below.
  }, [initialData]); // eslint-disable-line react-hooks/exhaustive-deps

  // URL → state: an external URL change (sidebar "Home" → bare /team/:id, a second deep link)
  // syncs into state. Bail-guarded so it no-ops on our own writes (no loop).
  // While a patient is open, and on the drawer's close, the sensitive filters the URL could not
  // carry are kept; a value the URL does carry still wins. The close also puts them back on the
  // URL here, from the same `next`: the writer below must never run on a navigation alone, or it
  // writes the previous filters over the new URL and the two effects undo each other.
  useEffect(() => {
    const keep = onPatient || closedPatient;
    const next = keep ? { ...sensitiveOf(selectedFilters), ...urlState.selectedFilters } : urlState.selectedFilters;
    setSelectedFilters(prev => (filtersEqual(prev, next) ? prev : next));
    setSort(prev => (prev.field === urlState.sort.field && prev.direction === urlState.sort.direction ? prev : urlState.sort));
    if (closedPatient) {
      const url = serializeToParams(searchParams, { selectedFilters: next, sort: urlState.sort });
      if (url.toString() !== searchParams.toString()) setSearchParams(url, { replace: true });
    }
  }, [urlState, onPatient, closedPatient]); // eslint-disable-line react-hooks/exhaustive-deps

  // state → URL: mirror filters+sort to the query. `searchParams` is read as the base but kept OUT
  // of the deps so an external nav isn't reverted (readback owns URL→state); diff-guarded, no loop.
  useEffect(() => {
    const omit = onPatient ? SENSITIVE_BOARD_KEYS : [];
    const next = serializeToParams(searchParams, { selectedFilters, sort }, { omit });
    if (next.toString() !== searchParams.toString()) setSearchParams(next, { replace: true });
  }, [selectedFilters, sort]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSortChange = useCallback((field, direction) => {
    setSort({ field, direction });
    setPage(p => ({ ...p, offset: 0 }));
  }, []);

  const handleSearchChange = useCallback((query) => {
    setSearchInput(query);
    setPage(p => ({ ...p, offset: 0 }));
  }, []);

  // The roster appends as the operator scrolls. `rows.length` is the cursor, not
  // `page.offset` — dedupe can drop a row, and asking the server for an offset we
  // already hold would re-append what's on screen.
  const total = Number(pagination?.total_count ?? 0);
  const hasMore = !exhausted && rows.length > 0 && rows.length < total;
  const loadMore = useCallback(() => {
    if (loadingMore || loading) return; // one in flight at a time
    setPage(p => (p.offset === rows.length ? p : { ...p, offset: rows.length }));
  }, [loadingMore, loading, rows.length]);

  // Re-fetch from the top, dropping the accumulated pages — refreshing a scrolled board
  // must replace the roster, never append page N+1 onto it.
  const refresh = useCallback(() => {
    fullRefreshRef.current = true;
    if (page.offset === 0) fetchPage(true); // no offset change → the effect won't fire
    else setPage(p => ({ ...p, offset: 0 }));
  }, [page.offset, fetchPage]);

  // Set the active values for a filter key. Empty/missing array removes the key so no stray param.
  const handleFiltersChange = useCallback((key, next) => {
    setSelectedFilters(prev => {
      const hasValues = Array.isArray(next) && next.length > 0;
      if (!hasValues) {
        if (!(key in prev)) return prev;
        const { [key]: _, ...rest } = prev;
        return rest;
      }
      return { ...prev, [key]: next };
    });
    setPage(p => ({ ...p, offset: 0 }));
  }, []);

  // A tile, card or chip click (logic/dashboard.ts applyBoardPick); a no-op pick neither resets the page nor refetches.
  const handleBoardPick = useCallback((pick) => {
    const next = applyBoardPick(selectedFilters, pick);
    if (next === selectedFilters) return;
    setSelectedFilters(next);
    setPage(p => ({ ...p, offset: 0 }));
  }, [selectedFilters]);

  // Optimistic local clear + persist the read-receipt server-side (without it the dot returns on refetch).
  const markRead = useCallback((userid) => {
    setRows(prev => prev.map(row =>
      row.userid === String(userid)
        ? { ...row, unread: { snippet: null, ...(row.unread || {}), has_unread: false } }
        : row
    ));
    markChatViewed(userid);
  }, [markChatViewed]);

  return {
    kpis, columns, includes, rows, pagination, loading,
    loadingMore, hasMore, loadMore,
    sort, searchInput,
    filters, selectedFilters,
    handleSortChange, handleSearchChange, handleFiltersChange, handleBoardPick,
    refresh,
    markRead,
  };
}
